import { usePrinterHooks } from '@justomx/atlas-components'
import { getWarehouseBySapCode } from '@justomx/warehouse-commons'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useLoaderData } from 'react-router-dom'
import { Analytics } from 'analytics/analytics.events'
import { updateOrderStatus } from 'client/stock-transfers/stock-transfers.client'
import { useRequiredParams } from 'hooks/params.hook'

import { useSessionData } from 'hooks/session.hook'
import type { PalletLabelData } from 'types/assembly/model'
import {
  OrderDetailSchema,
  type PreparationPalletData
} from 'types/assembly/model'
import { ZOD_ERROR } from 'utils/constants'
import { generatePalletLabel } from 'utils/pallet-label'
import { toastError, toastSuccess } from 'utils/toast'
import { useGoToAssembly } from '../assembly.navigator'
import { useGoToScanPrinter } from '../scan-printer/ScanPrinter.navigator'
import { useGoToTransfer } from '../transfer/Transfer.navigator'

export const usePreparation = () => {
  const { transferId, docNum } = useRequiredParams('transferId', 'docNum')
  const [scannedPallet, setScannedPallet] = useState<string | null>(null)
  const [totalSKUs, setTotalSKUs] = useState(0)
  const [loading, setLoading] = useState(false)
  const [palletsData, setPalletsData] = useState<
    Record<string, PreparationPalletData>
  >({})
  const transferPage = useGoToTransfer()
  const assemblyPage = useGoToAssembly()
  const scanPrinterPage = useGoToScanPrinter()
  const rawOrder = useLoaderData()
  const { user } = useSessionData()
  const { print, isConfigured } = usePrinterHooks()

  const [printerReady, setPrinterReady] = useState(false)
  const [warehouse, setWarehouse] = useState('')

  useEffect(() => {
    isConfigured().then((configured) => {
      setPrinterReady(configured)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const result = OrderDetailSchema.safeParse(rawOrder)
    if (!result.success) {
      console.info(result.error)
      throw Error(ZOD_ERROR)
    }

    const palletsMap: Record<string, PreparationPalletData> = {}
    let skuCounter = 0
    result.data.containers.forEach((container, index) => {
      if (container.products.length === 0) {
        return
      }

      skuCounter += container.totalSKUs
      palletsMap[container.id] = {
        container: container.id,
        productCount: container.products.length,
        location: container.location || '',
        currentPallet: index === 0
      }
    })
    setTotalSKUs(skuCounter)
    setPalletsData(palletsMap)
    setWarehouse(result.data.toWarehouse)
  }, [rawOrder, totalSKUs])

  const result = OrderDetailSchema.safeParse(rawOrder)
  if (!result.success) {
    console.info(result.error)
    throw Error(ZOD_ERROR)
  }

  const onGoBack = () => {
    assemblyPage.go()
  }

  const getNextPalletKey = (pallets: Record<string, PreparationPalletData>) => {
    return Object.keys(pallets).find((key) => !pallets[key].palletReady) ?? null
  }

  const markPalletAsScanned = (
    scanned: string,
    palletsData: Record<string, PreparationPalletData>
  ) => {
    const palletData = palletsData[scanned] ?? null
    if (!palletData) {
      toastError('No se encontró la tarima escaneada')
      return null
    } else if (palletData.palletReady) {
      toastError('La tarima ya ha sido escaneada')
      return null
    }
    return {
      ...palletsData,
      [scanned]: { ...palletData, palletScanned: true }
    }
  }

  const markPalletAsReady = (
    palletsData: Record<string, PreparationPalletData>,
    scannedPallet: string
  ) => {
    return {
      ...palletsData,
      [scannedPallet]: {
        ...palletsData[scannedPallet],
        palletReady: true,
        currentPallet: false
      }
    }
  }

  const updateNextCurrentPallet = (
    palletsData: Record<string, PreparationPalletData>
  ) => {
    const nextPalletKey = getNextPalletKey(palletsData)
    if (nextPalletKey) {
      return {
        ...palletsData,
        [nextPalletKey]: { ...palletsData[nextPalletKey], currentPallet: true }
      }
    }
    return palletsData
  }

  const isLastPallet = (
    pallets: Record<string, PreparationPalletData>,
    currentKey: string
  ) => {
    const updatedPallets = {
      ...pallets,
      [currentKey]: { ...pallets[currentKey], palletReady: true }
    }
    return Object.values(updatedPallets).every((pallet) => pallet.palletReady)
  }

  const onScan = async (scanned: string) => {
    if (!scannedPallet) {
      const updatedPallets = markPalletAsScanned(scanned, palletsData)
      if (!updatedPallets) {
        return
      }

      setScannedPallet(scanned)
      setPalletsData(updatedPallets)
      return
    }

    if (palletsData[scannedPallet]?.location === scanned) {
      const updatedPallets = markPalletAsReady(palletsData, scannedPallet)
      const finalPallets = updateNextCurrentPallet(updatedPallets)
      setPalletsData(finalPallets)

      if (isLastPallet(palletsData, scannedPallet)) {
        try {
          setLoading(true)
          const result = await updateOrderStatus(transferId, 'readyForLoading')
          Analytics.assembly.updateOrderStatus({
            transferId,
            docNum,
            statusFromBackend: result?.orderStatus || 'error',
            fromScreen: 'preparation'
          })
          transferPage.go(transferId, docNum)
        } catch (error) {
          console.error(error)
        } finally {
          setLoading(false)
        }
      }

      setScannedPallet(null)
      return
    }

    toastError('La ubicación escaneada no coincide con la tarima')
  }

  const replaceTwithGIfAtEnd = (input: string): string => {
    if (input.endsWith('T')) {
      return input.slice(0, -1) + 'G'
    }
    return input
  }

  const onPrintPallet = async (
    palletData: PreparationPalletData,
    palletNumber: number
  ) => {
    setLoading(true)
    const firstProduct = result.data.containers.find(
      (container) => container.id === palletData.container
    )?.products[0]

    const printDate = moment().format('DD/MM/YY-HH:mm')
    const warehouseCode = replaceTwithGIfAtEnd(result.data.toWarehouse)
    const warehouse = getWarehouseBySapCode(warehouseCode)

    const labelData: PalletLabelData = {
      docNum,
      palletNumber,
      warehouse: warehouse?.wmsName || result.data.toWarehouse,
      totalPallets: Object.values(palletsData).length,
      container: palletData.container,
      totalSKUs: palletData.productCount,
      printDate,
      userName: `${user.firstName} ${user.lastName}`,
      productsZone: firstProduct?.productZone ?? '',
      isStockOut: false
    }

    try {
      const label = generatePalletLabel(labelData)
      await print(label)
      toastSuccess('Imprimiendo etiqueta de tarima.')
      Analytics.assembly.printSuccess({
        transferId,
        docNum,
        zplLabel: label
      })
    } catch (error) {
      console.error(error)
      toastError('Error al imprimir etiqueta de tarima.')
    } finally {
      setLoading(false)
    }
  }

  const configurePrinter = () => {
    if (!printerReady) {
      scanPrinterPage.go(transferId, docNum, true)
    }
  }

  return {
    docNum,
    palletsData,
    totalSKUs,
    loading,
    printerReady,
    warehouse,
    actions: {
      onGoBack,
      onScan,
      onPrintPallet,
      configurePrinter
    }
  }
}
